import * as echarts from 'echarts';
import { FORECAST_MODELS } from '@app/common/static-data/models';

export interface ChartConfigParams {
  isDark: boolean;
  axisColor: string;
  sortedModels: string[];
  variable: { displayUnit: string };
  yAxisRange: { min: number | undefined; max: number | undefined };
  series: any[];
  timezone: string;
  formatInUserTimezone: (timestamp: string, format: string) => string;
}

export function buildModernChartConfig({
  isDark,
  axisColor,
  sortedModels,
  variable,
  yAxisRange,
  series,
  timezone,
  formatInUserTimezone,
}: ChartConfigParams): echarts.EChartsOption {
  // Determine total number of days in range from provided series
  let minTimestamp: number | null = null;
  let maxTimestamp: number | null = null;

  series?.forEach((s: any) => {
    const data: any[] = s?.data || [];
    data.forEach((point: any) => {
      // Support both [time, value] and { value: [time, value] } formats
      const t = Array.isArray(point)
        ? Number(point[0])
        : point && point.value && Array.isArray(point.value)
          ? Number(point.value[0])
          : null;
      if (t != null && !isNaN(t)) {
        if (minTimestamp == null || t < minTimestamp) minTimestamp = t;
        if (maxTimestamp == null || t > maxTimestamp) maxTimestamp = t;
      }
    });
  });

  const MS_PER_DAY = 24 * 3600 * 1000;
  const totalDays =
    minTimestamp != null && maxTimestamp != null
      ? Math.ceil((maxTimestamp - minTimestamp) / MS_PER_DAY)
      : 0;
  const showEverySecondDay = totalDays > 30;

  return {
    backgroundColor: 'transparent',
    grid: {
      left: '3%',
      right: '3%',
      bottom: '8%',
      top: '12%',
      containLabel: true,
    },
    tooltip: buildTooltipConfig(isDark, variable, timezone, formatInUserTimezone),
    legend: buildLegendConfig(sortedModels, axisColor, isDark),
    dataZoom: buildDataZoomConfig(isDark, axisColor),
    xAxis: buildXAxisConfig(axisColor, timezone, formatInUserTimezone, showEverySecondDay),
    yAxis: buildYAxisConfig(axisColor, variable, yAxisRange),
    series,
  };
}

export function buildEmbeddedChartConfig({
  isDark,
  axisColor,
  sortedModels,
  variable,
  yAxisRange,
  series,
  timezone,
  formatInUserTimezone,
}: ChartConfigParams): echarts.EChartsOption {
  // Determine total number of days in range from provided series
  let minTimestamp: number | null = null;
  let maxTimestamp: number | null = null;

  series?.forEach((s: any) => {
    const data: any[] = s?.data || [];
    data.forEach((point: any) => {
      const t = Array.isArray(point)
        ? Number(point[0])
        : point && point.value && Array.isArray(point.value)
          ? Number(point.value[0])
          : null;
      if (t != null && !isNaN(t)) {
        if (minTimestamp == null || t < minTimestamp) minTimestamp = t;
        if (maxTimestamp == null || t > maxTimestamp) maxTimestamp = t;
      }
    });
  });

  const MS_PER_DAY = 24 * 3600 * 1000;
  const totalDays =
    minTimestamp != null && maxTimestamp != null
      ? Math.ceil((maxTimestamp - minTimestamp) / MS_PER_DAY)
      : 0;
  const showEverySecondDay = totalDays > 30;

  return {
    backgroundColor: 'transparent',
    grid: {
      left: '20',
      right: '20',
      top: '20',
      bottom: '20',
      containLabel: true,
    },
    tooltip: buildTooltipConfig(isDark, variable, timezone, formatInUserTimezone),
    legend: buildLegendConfig(sortedModels, axisColor, isDark),
    xAxis: buildXAxisConfig(axisColor, timezone, formatInUserTimezone, showEverySecondDay),
    yAxis: buildYAxisConfig(axisColor, variable, yAxisRange),
    series,
  };
}

function buildTooltipConfig(
  isDark: boolean,
  variable: { displayUnit: string },
  timezone: string,
  formatInUserTimezone: (timestamp: string, format: string) => string,
) {
  return {
    trigger: 'axis' as const,
    backgroundColor: isDark ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    borderColor: isDark ? '#374151' : '#E5E7EB',
    borderWidth: 1,
    textStyle: {
      color: isDark ? '#F9FAFB' : '#111827',
      fontSize: 13,
    },
    padding: [12, 16],
    extraCssText: `
      border-radius: 8px;
      box-shadow: 0 4px 12px ${isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.1)'};
      backdrop-filter: blur(8px);
    `,
    formatter: (params: any) => {
      if (!params || params.length === 0) return '';

      const time =
        timezone === 'UTC'
          ? new Date(params[0].value[0]).toLocaleString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
              timeZoneName: 'short',
            })
          : formatInUserTimezone(new Date(params[0].value[0]).toISOString(), 'MMM d, HH:mm zzz');

      let result = `<div style="font-weight: 600; margin-bottom: 8px; color: ${isDark ? '#F9FAFB' : '#111827'};">${time}</div>`;

      params.forEach((param: any) => {
        // Skip confidence band series
        if (
          param.seriesName === 'Confidence Band Base' ||
          param.seriesName === 'Confidence Band Width' ||
          param.seriesName.includes('_lower') ||
          param.seriesName.includes('_area')
        ) {
          return;
        }

        if (param.value[1] !== '-' && param.value[1] !== undefined) {
          const mainValue = param.value[1];
          const tooltipText = `${param.seriesName}: ${mainValue.toLocaleString('en-US')} ${variable.displayUnit}`;

          result += `<div style="font-size: 13px; color: ${param.color}; margin: 4px 0;">
            <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${param.color}; margin-right: 8px;"></span>
            <strong>${tooltipText}</strong>
          </div>`;

          // Check if quantiles are available
          if (
            param.data &&
            (param.data.q5 !== undefined ||
              param.data.q25 !== undefined ||
              param.data.q75 !== undefined ||
              param.data.q95 !== undefined)
          ) {
            result += `<div style="font-size: 12px; color: ${param.color}; margin: 1px 0 4px 32px;">
              <strong>Quantiles (${variable.displayUnit})</strong>
            </div>`;

            // Collect all available quantile values to determine formatting
            const quantiles = [];
            if (param.data.q5 !== undefined)
              quantiles.push({ label: '&nbsp5th:', value: param.data.q5 });
            if (param.data.q25 !== undefined)
              quantiles.push({ label: '25th:', value: param.data.q25 });
            if (param.data.q75 !== undefined)
              quantiles.push({ label: '75th:', value: param.data.q75 });
            if (param.data.q95 !== undefined)
              quantiles.push({ label: '95th:', value: param.data.q95 });

            // Find the maximum width needed for the integer part to align decimal points
            const maxIntegerWidth = Math.max(
              ...quantiles.map(
                (q) => Math.floor(Math.abs(q.value)).toString().length + (q.value < 0 ? 1 : 0),
              ),
            );

            quantiles.forEach((q) => {
              // Format with fixed decimal places and right-align the integer part
              const formattedValue = q.value.toFixed(2);
              const [integerPart, decimalPart] = formattedValue.split('.');
              const paddedInteger = integerPart.padStart(maxIntegerWidth, '\u00A0'); // Use non-breaking space for padding

              result += `<div style="font-size: 12px; color: ${param.color}; margin: 1px 0 1px 48px; font-family: monospace;">
                ${q.label} ${paddedInteger}.${decimalPart}
              </div>`;
            });
          }
        }
      });
      return result;
    },
  };
}

function buildLegendConfig(sortedModels: string[], axisColor: string, isDark: boolean) {
  return {
    data: sortedModels.map(
      (modelId) => FORECAST_MODELS.find((m) => m.id === modelId)?.label || modelId,
    ),
    textStyle: {
      color: axisColor,
      fontSize: 13,
      fontWeight: 500,
    },
    top: 0,
    padding: [12, 0],
    itemGap: 20,
    type: 'scroll' as const,
    width: '95%',
    selectedMode: true,
    orient: 'horizontal' as const,
    pageButtonPosition: 'end' as const,
    pageButtonGap: 20,
    pageIconColor: isDark ? '#E5E7EB' : '#374151',
    pageIconInactiveColor: isDark ? '#6B7280' : '#9CA3AF',
    pageTextStyle: {
      color: axisColor,
    },
    itemStyle: {
      borderWidth: 0,
    },
  };
}

function buildDataZoomConfig(isDark: boolean, axisColor: string) {
  return [
    {
      type: 'slider' as const,
      show: true,
      xAxisIndex: [0],
      bottom: '1%',
      height: 16,
      borderColor: 'transparent',
      backgroundColor: isDark ? 'rgba(55, 65, 81, 0.3)' : 'rgba(243, 244, 246, 0.8)',
      fillerColor: isDark ? 'rgba(236, 72, 153, 0.3)' : 'rgba(236, 72, 153, 0.2)',
      textStyle: {
        color: axisColor,
        fontSize: 11,
      },
      handleStyle: {
        color: '#EC4899',
        borderColor: '#EC4899',
        borderWidth: 2,
      },
      moveHandleStyle: {
        color: isDark ? '#6B7280' : '#9CA3AF',
      },
      selectedDataBackground: {
        lineStyle: {
          color: 'transparent',
        },
        areaStyle: {
          color: 'transparent',
        },
      },
      dataBackground: {
        lineStyle: {
          color: 'transparent',
        },
        areaStyle: {
          color: 'transparent',
        },
      },
      start: 0,
      end: 100,
      filterMode: 'none' as const,
    },
    {
      type: 'inside' as const,
      xAxisIndex: [0],
      zoomOnMouseWheel: true,
      moveOnMouseMove: true,
      filterMode: 'none' as const,
    },
  ];
}

function buildXAxisConfig(
  axisColor: string,
  timezone: string,
  formatInUserTimezone: (timestamp: string, format: string) => string,
  showEverySecondDay: boolean,
) {
  return {
    type: 'time' as const,
    axisLabel: {
      formatter: (value: number, index: number) => {
        if (showEverySecondDay && index % 2 === 1) {
          return '';
        }
        const date = new Date(value);
        if (timezone === 'UTC') {
          return date.toLocaleString('en-US', {
            day: 'numeric',
            month: 'short',
          });
        } else {
          return formatInUserTimezone(date.toISOString(), 'MMM dd');
        }
      },
      color: axisColor,
      fontSize: 12,
      fontWeight: 500,
      margin: 16,
      hideOverlap: true,
    },
    splitLine: {
      show: false, // Remove gridlines
    },
    axisLine: {
      show: false, // Remove axis line for cleaner look
    },
    axisTick: {
      show: false, // Remove axis ticks
    },
    minInterval: 3600 * 24 * 1000,
    maxInterval: 3600 * 24 * 1000,
    splitNumber: 20,
    boundaryGap: ['2%', '2%'],
  };
}

function buildYAxisConfig(
  axisColor: string,
  variable: { displayUnit: string },
  yAxisRange: { min: number | undefined; max: number | undefined },
) {
  return {
    type: 'value' as const,
    name: variable.displayUnit,
    nameLocation: 'middle' as const,
    nameGap: 35,
    nameTextStyle: {
      color: axisColor,
      fontSize: 12,
      fontWeight: 500,
    },
    min: yAxisRange.min,
    max: yAxisRange.max,
    axisLabel: {
      color: axisColor,
      fontSize: 12,
      fontWeight: 500,
      formatter: (value: number) => {
        if (Math.abs(value) >= 1000000) {
          return (value / 1000000).toFixed(1) + 'M';
        } else if (Math.abs(value) >= 1000) {
          return (value / 1000).toFixed(1) + 'k';
        }
        return value.toFixed(1);
      },
    },
    splitLine: {
      show: false, // Remove gridlines
    },
    axisLine: {
      show: false, // Remove axis line
    },
    axisTick: {
      show: false, // Remove axis ticks
    },
  };
}
